import { useLocalStorage } from '@mantine/hooks'
import type { AuthSessionStorage } from '@utils/auth-utils'
import { deserializeLocalStorage } from '@utils/localstorage-utils'
import { z } from 'zod'

export const getCurrentOverrides = (sessions: AuthSessionStorage) => {
  const tenantId = localStorage.getItem('tenant-id')?.replaceAll('"', '')
  if (!tenantId) return {}

  const tenantInfos = Object.values(sessions).flatMap((session) => {
    return session.latestAccountInfo.teams
      .filter((team) => team.tenantId === tenantId)
      .map((entry) => ({
        ...entry,
        lastRefreshTime: session.lastRefreshTime,
      }))
      .filter((entry) => entry.tenant.settings?.i18n?.overridesByIdentifier)
  })

  const latest = tenantInfos.toSorted(
    (a, b) =>
      new Date(b.lastRefreshTime).getTime() -
      new Date(a.lastRefreshTime).getTime()
  )[0]

  return latest?.tenant.settings?.i18n?.overridesByIdentifier ?? {}
}

export const useSettingsLocalStorage = () => {
  const [localLanguage, setLocalLanguage] = useLocalStorage<string | undefined>(
    {
      defaultValue: undefined,
      deserialize: (value) =>
        deserializeLocalStorage(value, z.enum(['auto', 'de', 'en']), undefined),
      getInitialValueInEffect: false,
      key: 'language',
    }
  )

  const [shouldReloadLocale, setShouldReloadLocale] = useLocalStorage<boolean>({
    defaultValue: false,
    getInitialValueInEffect: false,
    key: 'shouldReloadLocale',
  })

  return {
    localLanguage,
    setLocalLanguage,
    shouldReloadLocale,
    setShouldReloadLocale,
  }
}
